import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MessageCircle, Send } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_URL = "https://wa.me/32451075502";
const EMAIL_URL = "mailto:irislanguagesacademy@hotmail.com";

const Contact = () => {
  const { t } = useLanguage();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", language: c.languageOptions[0], lesson_format: c.formats[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const valid = form.name.trim().length >= 2 && /\S+@\S+\.\S+/.test(form.email) && form.message.trim().length >= 5;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form, { timeout: 20000 });
      toast.success(c.success, { duration: 5000 });
      setForm({ name: "", email: "", language: c.languageOptions[0], lesson_format: c.formats[0], message: "" });
    } catch {
      toast.error(c.error, { duration: 6000 });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-black/10 bg-[#FDFBF7] px-5 py-3.5 text-sm text-[#1A1A1A] placeholder:text-[#5C5C5C]/50 transition-colors duration-300 focus:border-[#45B7D1] focus:outline-none";

  return (
    <section id="contact" data-testid="section-contact" className="relative overflow-hidden py-24 md:py-32">
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[26rem] w-[26rem] rounded-full bg-gradient-to-tr from-[#FF6B6B]/25 via-[#F9D876]/25 to-[#4ECDC4]/30 blur-3xl" aria-hidden="true" />

      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-[#5C5C5C]">{c.eyebrow}</p>
            <h2 className="mt-5 font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#1A1A1A] md:text-5xl">
              {c.title}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#5C5C5C] md:text-lg">{c.sub}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-col gap-4">
              <motion.a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-link"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center gap-4 rounded-3xl border border-black/[0.06] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-colors duration-300 hover:border-black/15"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#25D366]/10">
                  <MessageCircle className="h-6 w-6 text-[#1FAF54]" />
                </span>
                <span>
                  <span className="block font-medium text-[#1A1A1A]">{c.whatsapp}</span>
                  <span className="block text-sm text-[#5C5C5C]">+32 451 075 502</span>
                </span>
              </motion.a>

              <motion.a
                href={EMAIL_URL}
                data-testid="email-link"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center gap-4 rounded-3xl border border-black/[0.06] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-colors duration-300 hover:border-black/15"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#45B7D1]/10">
                  <Mail className="h-6 w-6 text-[#45B7D1]" />
                </span>
                <span className="min-w-0">
                  <span className="block font-medium text-[#1A1A1A]">{c.email}</span>
                  <span className="block truncate text-sm text-[#5C5C5C]">irislanguagesacademy@hotmail.com</span>
                </span>
              </motion.a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.05}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-[2rem] border border-black/[0.06] bg-white p-8 shadow-[0_16px_50px_rgb(0,0,0,0.06)] md:p-10"
            >
              <p className="font-display text-2xl font-semibold tracking-tight text-[#1A1A1A]">{c.formTitle}</p>

              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-[#5C5C5C]">{c.name}</span>
                  <input
                    type="text"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder={c.namePh}
                    data-testid="contact-name-input"
                    className={inputCls}
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-[#5C5C5C]">{c.emailLabel}</span>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder={c.emailPh}
                    data-testid="contact-email-input"
                    className={inputCls}
                  />
                </label>
              </div>

              <label className="mt-5 block">
                <span className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-[#5C5C5C]">{c.language}</span>
                <select
                  value={form.language}
                  onChange={set("language")}
                  data-testid="contact-language-select"
                  className={`${inputCls} appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%235C5C5C%22 stroke-width=%222%22%3E%3Cpath d=%22m6 9 6 6 6-6%22/%3E%3C/svg%3E')] bg-[position:right_1.25rem_center] bg-no-repeat pr-12`}
                >
                  {c.languageOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </label>

              <div className="mt-5">
                <span className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-[#5C5C5C]">{c.format}</span>
                <div className="flex flex-wrap gap-2.5">
                  {c.formats.map((fmt) => (
                    <button
                      key={fmt}
                      type="button"
                      data-testid={`contact-format-${fmt.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setForm((f) => ({ ...f, lesson_format: fmt }))}
                      className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                        form.lesson_format === fmt
                          ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FDFBF7]"
                          : "border-black/10 bg-[#FDFBF7] text-[#5C5C5C] hover:border-black/25"
                      }`}
                    >
                      {fmt}
                    </button>
                  ))}
                </div>
              </div>

              <label className="mt-5 block">
                <span className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-[#5C5C5C]">{c.message}</span>
                <textarea
                  required
                  minLength={5}
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder={c.messagePh}
                  data-testid="contact-message-input"
                  className={`${inputCls} resize-none`}
                />
              </label>

              <button
                type="submit"
                disabled={sending || !valid}
                data-testid="contact-submit-button"
                className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#1A1A1A] px-8 py-4 text-sm font-medium text-[#FDFBF7] transition-all duration-300 hover:bg-[#333] disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
              >
                {sending ? c.sending : c.submit}
                <Send className="h-4 w-4" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
